import type { JSX } from 'react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import {
  Camera,
  Download,
  Maximize2,
  Minimize2,
  Orbit,
  Power,
  User,
  Video,
  VideoOff
} from 'lucide-react'
import type { BotDetail, ViewerPositionFrame } from '@apiary/shared'
import type { CameraMode } from '@apiary/viewer-core'
import { VIEW_DISTANCE_DEFAULT, VIEWER_ASSETS_FALLBACK_VERSION } from '@apiary/shared'
import { cn } from '@/lib/cn'
import { coordsOf } from '@/ui/format'
import { problemText } from '@/screens/shared/problem'
import { useViewerSession } from './viewer/useViewerSession'
import type { ViewerHudStats } from './viewer/BotViewerCanvas'
import { Button, IconButton } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { Surface } from '@/ui/primitives/Surface'
import { useCameraExpand } from './camera-expand'

/**
 * Камера бота (L2, док 08) с разворотом почти на весь экран.
 *
 * Вся механика разворота — в `camera-expand.ts`: там же объяснено, почему узел
 * не переносится по DOM (WebGL-контекст) и почему `position: fixed` приходится
 * задавать инлайновым стилем, а не классом.
 */

const BotViewerCanvas = lazy(() => import('./viewer/BotViewerCanvas'))

export interface CameraPanelProps {
  botId: string
  botName: string
  detail: BotDetail | null
  /** Камера доступна только у бота в игре (FR-V5). */
  playing: boolean
  /** Открыть камеру сразу при монтировании — по `?camera=1` из списка. */
  autoOpen?: boolean
}

export function CameraPanel({
  botId,
  botName,
  detail,
  playing,
  autoOpen = false
}: CameraPanelProps): JSX.Element {
  const viewer = useViewerSession(botId, playing)
  const expand = useCameraExpand()

  const [mode, setMode] = useState<CameraMode>('orbit')
  const [stats, setStats] = useState<ViewerHudStats | null>(null)
  const [position, setPosition] = useState<ViewerPositionFrame | null>(null)
  const shotRef = useRef<(() => Promise<Blob | null>) | null>(null)
  /**
   * Сцена не поднялась (не скачались текстуры блоков, нет WebGL).
   * Раньше такое падение проглатывалось, и человек видел просто чёрный
   * прямоугольник — ровно так выглядела ошибка версии ассетов на проде.
   */
  const [sceneError, setSceneError] = useState<unknown>(null)

  const open = viewer.status === 'open' && viewer.session !== null

  // Автооткрытие по `?camera=1`. Условие `status === 'idle'` само по себе
  // защищает от повторов: как только сессия открылась, статус уже не idle.
  useEffect(() => {
    if (autoOpen && playing && viewer.status === 'idle') viewer.open(VIEW_DISTANCE_DEFAULT)
  }, [autoOpen, playing, viewer])

  const download = async (): Promise<void> => {
    const blob = await shotRef.current?.()
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${botName}-${Date.now()}.png`
    link.click()
    URL.revokeObjectURL(url)
  }

  const hud = {
    health: detail?.health ?? null,
    food: detail?.food ?? null,
    coords: coordsOf(position?.pos ?? detail?.pos),
    dimension: detail?.dimension ?? null,
    chunks: stats?.chunks ?? null,
    fps: stats?.fps ?? null,
    bytes: stats?.bytes ?? null
  }

  return (
    <>
      {/* Распорка держит место в потоке, пока камера развёрнута. */}
      {expand.placeholder === null ? null : (
        <div aria-hidden style={{ height: expand.placeholder }} />
      )}

      {/* Подложка: отделяет развёрнутую камеру от интерфейса и сворачивает по клику. */}
      {expand.expanded ? (
        <button
          type="button"
          aria-label="Свернуть камеру"
          onClick={expand.toggle}
          style={expand.backdropStyle}
          className="anim-backdrop cursor-default bg-abyss/70"
        />
      ) : null}

      <Surface
        ref={expand.boxRef}
        variant="raised"
        style={expand.style}
        className={cn('flex flex-col', expand.expanded && 'shadow-lg')}
      >
        <div className="flex min-h-11 flex-wrap items-center gap-2 border-b border-line px-3 py-2">
          <span className="flex items-center gap-2 font-display text-base font-semibold text-ink">
            <Camera aria-hidden className="size-4 text-ink-faint" />
            Камера
          </span>

          {open ? (
            <Chip tone="live" dot mono>
              {hud.fps === null ? '—' : `${Math.round(hud.fps)} FPS`}
            </Chip>
          ) : null}
          {hud.bytes === null ? null : (
            <Chip mono title="Трафик за сессию">
              {formatBytes(hud.bytes)}
            </Chip>
          )}

          <div className="ml-auto flex items-center gap-1">
            {open ? (
              <>
                <div
                  role="group"
                  aria-label="Режим камеры"
                  className="mr-1 flex gap-0.5 rounded-sm border border-line bg-panel-2 p-0.5"
                >
                  {(
                    [
                      { key: 'first', label: '1-е лицо', icon: User },
                      { key: 'orbit', label: 'Орбита', icon: Orbit }
                    ] as const
                  ).map(item => (
                    <button
                      key={item.key}
                      type="button"
                      aria-pressed={mode === item.key}
                      onClick={() => setMode(item.key)}
                      className={cn(
                        'press flex items-center gap-1.5 rounded-xs px-2 py-1 text-2xs',
                        mode === item.key ? 'bg-panel-3 text-ink' : 'text-ink-muted hover:text-ink'
                      )}
                    >
                      <item.icon aria-hidden className="size-3.5" />
                      {item.label}
                    </button>
                  ))}
                </div>

                <IconButton label="Снимок кадра" onClick={() => void download()}>
                  <Download aria-hidden className="size-4" />
                </IconButton>
              </>
            ) : null}

            {/* Разворот — там, где его ищут: в правом верхнем углу панели. */}
            <IconButton
              label={expand.expanded ? 'Свернуть камеру' : 'Развернуть камеру'}
              onClick={expand.toggle}
              disabled={!open}
            >
              {expand.expanded ? (
                <Minimize2 aria-hidden className="size-4" />
              ) : (
                <Maximize2 aria-hidden className="size-4" />
              )}
            </IconButton>

            {open ? (
              <IconButton label="Выключить камеру" tone="danger" onClick={viewer.close}>
                <VideoOff aria-hidden className="size-4" />
              </IconButton>
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={!playing || viewer.status === 'opening'}
                loading={viewer.status === 'opening'}
                onClick={() => viewer.open(VIEW_DISTANCE_DEFAULT)}
              >
                <Video aria-hidden className="size-4" />
                Включить
              </Button>
            )}
          </div>
        </div>

        {/* Сцена. В развёрнутом виде занимает всё оставшееся место панели. */}
        <div
          className={cn(
            'relative min-h-0 bg-abyss',
            expand.expanded ? 'flex-1' : 'aspect-video w-full'
          )}
        >
          {open && viewer.session !== null ? (
            <Suspense fallback={<Placeholder text="Загружаем движок…" />}>
              <BotViewerCanvas
                sessionId={viewer.session.sessionId}
                /*
                 * Версию ассетов называет САМА сессия, а не список поддерживаемых
                 * версий ядра. Разница дорогая: `supportedVersions[0]` — это
                 * «1.20.4», ассетов для неё в образе нет (лежит только 1.21.11),
                 * и загрузка блоков молча падала на 404 — сцена не поднималась,
                 * камера оставалась чёрной. Пока `/api/system/info` был закрыт на
                 * edge, `system.data` был undefined и спасал фоллбэк; как только
                 * эндпоинт починили, баг вылез наружу.
                 */
                assetsVersion={viewer.session.assetsVersion || VIEWER_ASSETS_FALLBACK_VERSION}
                mode={mode}
                visible={viewer.visible}
                onError={setSceneError}
                onStats={setStats}
                onPosition={setPosition}
                onReady={api => {
                  shotRef.current = api.screenshot
                  setSceneError(null)
                  // Сцена готова и слушает кадры — только теперь просим сервер
                  // выгружать мир: пачка колонок приходит один раз.
                  viewer.markCanvasReady()
                }}
              />
              {sceneError === null ? null : (
                <div
                  role="alert"
                  className="pointer-events-none absolute inset-x-0 bottom-0 bg-abyss/85 px-3 py-2 text-2xs text-fail"
                >
                  Сцена не загрузилась: {problemText(sceneError)}
                </div>
              )}
            </Suspense>
          ) : (
            <Placeholder
              text={
                !playing
                  ? 'Камера доступна, только когда бот в игре'
                  : viewer.status === 'error'
                    ? 'Не удалось открыть камеру'
                    : viewer.closeReason !== null
                      ? `Сессия закрыта: ${viewer.closeReason}`
                      : 'Камера выключена — она тратит трафик и CPU бота'
              }
              icon={playing ? <Power aria-hidden className="size-5" /> : undefined}
            />
          )}
        </div>

        {/* HUD: показатели бота под сценой, чтобы не закрывать кадр. */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-line px-3 py-2.5 text-2xs sm:grid-cols-4">
          <HudCell
            label="Здоровье"
            value={hud.health === null ? null : String(Math.round(hud.health))}
          />
          <HudCell
            label="Сытость"
            value={hud.food === null ? null : String(Math.round(hud.food))}
          />
          <HudCell label="Координаты" value={hud.coords} />
          <HudCell label="Измерение" value={hud.dimension} />
          <HudCell label="Колонок" value={hud.chunks === null ? null : String(hud.chunks)} />
          <HudCell label="Версия" value={detail?.version ?? null} />
          <HudCell
            label="Ping"
            value={detail?.ping === null || detail?.ping === undefined ? null : `${detail.ping} мс`}
          />
          <HudCell
            label="Игроков рядом"
            value={
              detail?.playersOnline === null || detail?.playersOnline === undefined
                ? null
                : String(detail.playersOnline)
            }
          />
        </dl>
      </Surface>
    </>
  )
}

function HudCell({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-ink-muted">{label}</dt>
      <dd
        className={cn(
          'truncate font-mono tabular-nums',
          value === null ? 'text-ink-faint' : 'text-ink'
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  )
}

function Placeholder({ text, icon }: { text: string; icon?: JSX.Element }): JSX.Element {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-2 px-6 text-center">
      {icon === undefined ? null : <span className="text-ink-faint">{icon}</span>}
      <p className="max-w-sm text-base text-ink-muted">{text}</p>
    </div>
  )
}

/** `816 КБ` — трафик сессии камеры. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
}
